'use client';

/**
 * CookieConsentContext
 *
 * Layout'ta bir kez alınan cookie-consent ve cookie-services verisini
 * uygulama genelinde paylaşır. Her sayfa geçişinde /api/cookie-consent ve
 * /api/cookie-services isteği atılmaz.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import type {
  CookieConsentResponse,
  CookieConsentAttributes,
  CookieServiceAttributes,
  CookiePreferences,
} from '@/types/cookie-consent';
import {
  getCookiePreferences,
  setCookiePreferences,
  needsReconsent,
} from '@/components/cookie-consent/cookie-utils';
import { fetchCookieConsentBootstrap } from '@/lib/cookie-consent-bootstrap';

const loadedScripts = new Map<string, HTMLScriptElement>();

function injectScript(service: CookieServiceAttributes, position: 'head' | 'body'): void {
  const scriptKey = `${service.key}_${position}`;
  if (loadedScripts.has(scriptKey)) return;
  const scriptContent = position === 'head' ? service.script_head : service.script_body;
  if (!scriptContent) return;

  const script = document.createElement('script');
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = scriptContent;
  const scriptElement = tempDiv.querySelector('script');

  if (scriptElement) {
    Array.from(scriptElement.attributes).forEach((attr) => {
      script.setAttribute(attr.name, attr.value);
    });
    script.textContent = scriptElement.textContent;
  } else {
    script.textContent = scriptContent;
  }

  if (service.load_strategy === 'async') script.async = true;
  else if (service.load_strategy === 'defer') script.defer = true;

  const target = position === 'head' ? document.head : document.body;
  target.appendChild(script);
  loadedScripts.set(scriptKey, script);
}

function removeScript(serviceKey: string, position: 'head' | 'body'): void {
  const scriptKey = `${serviceKey}_${position}`;
  const script = loadedScripts.get(scriptKey);
  if (script?.parentNode) {
    script.parentNode.removeChild(script);
    loadedScripts.delete(scriptKey);
  }
}

function removeAllScripts(): void {
  loadedScripts.forEach((script, key) => {
    if (script.parentNode) script.parentNode.removeChild(script);
  });
  loadedScripts.clear();
}

export interface CookieConsentContextValue {
  consent: CookieConsentResponse | null;
  consentAttributes: CookieConsentAttributes | null;
  services: CookieServiceAttributes[];
  preferences: CookiePreferences | null;
  loading: boolean;
  showBanner: boolean;
  showModal: boolean;
  setShowBanner: (show: boolean) => void;
  setShowModal: (show: boolean) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (categories: CookiePreferences['categories']) => void;
  loadServicesByCategory: (category: string) => CookieServiceAttributes[];
  injectScripts: (preferences: CookiePreferences | null) => void;
  removeScripts: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined);

export interface CookieConsentProviderProps {
  children: React.ReactNode;
  initialCookieConsent: CookieConsentResponse | null;
  initialCookieServices: CookieServiceAttributes[] | null;
}

export function CookieConsentProvider({
  children,
  initialCookieConsent,
  initialCookieServices,
}: CookieConsentProviderProps) {
  const [consent, setConsent] = useState<CookieConsentResponse | null>(initialCookieConsent);
  const [services, setServices] = useState<CookieServiceAttributes[]>(initialCookieServices ?? []);
  const [preferences, setPreferencesState] = useState<CookiePreferences | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [bootstrapDone, setBootstrapDone] = useState(() => {
    const hasConsent = Boolean(initialCookieConsent?.data?.attributes);
    const hasServices = (initialCookieServices?.length ?? 0) > 0;
    return hasConsent && hasServices;
  });

  useEffect(() => {
    setPreferencesState(getCookiePreferences());
  }, []);

  // Layout'tan eksik geldiyse client'ta tek seferlik bootstrap (dedupe)
  useEffect(() => {
    if (bootstrapDone) return;
    let alive = true;
    (async () => {
      try {
        const { consent: nextConsent, services: nextServices } = await fetchCookieConsentBootstrap();
        if (!alive) return;
        if (nextConsent?.data?.attributes) setConsent(nextConsent);
        if (nextServices.length > 0) setServices(nextServices);
      } catch {
        // Sessiz
      } finally {
        if (alive) setBootstrapDone(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [bootstrapDone]);

  useEffect(() => {
    if (!consent?.data?.attributes) return;
    const stored = getCookiePreferences();
    const shouldShow =
      !stored ||
      needsReconsent(stored?.version ?? null, consent.data.attributes.version) ||
      consent.needs_reconsent;
    if (shouldShow) setShowBanner(true);
  }, [consent]);

  const logPreferences = useCallback(
    async (prefs: CookiePreferences, action: 'accept_all' | 'reject_all' | 'custom_save') => {
      try {
        await fetch('/api/cookie-consent/preferences', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preferences: prefs, action }),
        });
      } catch {
        // Sessiz
      }
    },
    []
  );

  const acceptAll = useCallback(() => {
    if (!consent?.data?.attributes) return;
    const newPreferences: CookiePreferences = {
      consentGiven: true,
      timestamp: Date.now(),
      version: consent.data.attributes.version,
      categories: {
        necessary: true,
        analytics: true,
        marketing: true,
        functional: true,
      },
    };
    setCookiePreferences(newPreferences);
    setPreferencesState(newPreferences);
    setShowBanner(false);
    setShowModal(false);
    logPreferences(newPreferences, 'accept_all');
  }, [consent, logPreferences]);

  const rejectAll = useCallback(() => {
    if (!consent?.data?.attributes) return;
    const newPreferences: CookiePreferences = {
      consentGiven: false,
      timestamp: Date.now(),
      version: consent.data.attributes.version,
      categories: {
        necessary: true,
        analytics: false,
        marketing: false,
        functional: false,
      },
    };
    setCookiePreferences(newPreferences);
    setPreferencesState(newPreferences);
    setShowBanner(false);
    setShowModal(false);
    logPreferences(newPreferences, 'reject_all');
  }, [consent, logPreferences]);

  const savePreferences = useCallback(
    (categories: CookiePreferences['categories']) => {
      if (!consent?.data?.attributes) return;
      const newPreferences: CookiePreferences = {
        consentGiven: true,
        timestamp: Date.now(),
        version: consent.data.attributes.version,
        categories: { ...categories, necessary: true },
      };
      setCookiePreferences(newPreferences);
      setPreferencesState(newPreferences);
      setShowBanner(false);
      setShowModal(false);
      logPreferences(newPreferences, 'custom_save');
    },
    [consent, logPreferences]
  );

  const loadServicesByCategory = useCallback(
    (category: string): CookieServiceAttributes[] => {
      return services.filter((s) => s.category === category);
    },
    [services]
  );

  const injectScripts = useCallback(
    (prefs: CookiePreferences | null) => {
      if (!prefs || !prefs.consentGiven) {
        const necessaryServices = services.filter((s) => s.category === 'necessary');
        necessaryServices.forEach((service) => {
          if (service.script_head) injectScript(service, 'head');
          if (service.script_body) injectScript(service, 'body');
        });
        return;
      }
      const categories = ['necessary', 'analytics', 'marketing', 'functional'] as const;
      categories.forEach((category) => {
        const categoryServices = services.filter((s) => s.category === category);
        if (prefs.categories[category]) {
          categoryServices.forEach((service) => {
            if (service.script_head) injectScript(service, 'head');
            if (service.script_body) injectScript(service, 'body');
          });
        } else {
          categoryServices.forEach((service) => {
            removeScript(service.key, 'head');
            removeScript(service.key, 'body');
          });
        }
      });
    },
    [services]
  );

  const removeScripts = useCallback(() => {
    removeAllScripts();
  }, []);

  const value: CookieConsentContextValue = useMemo(
    () => ({
      consent,
      consentAttributes: consent?.data?.attributes ?? null,
      services,
      preferences,
      loading: false,
      showBanner,
      showModal,
      setShowBanner,
      setShowModal,
      acceptAll,
      rejectAll,
      savePreferences,
      loadServicesByCategory,
      injectScripts,
      removeScripts,
    }),
    [
      consent,
      services,
      preferences,
      showBanner,
      showModal,
      acceptAll,
      rejectAll,
      savePreferences,
      loadServicesByCategory,
      injectScripts,
      removeScripts,
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsentContext(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (ctx === undefined) {
    throw new Error('useCookieConsentContext must be used within CookieConsentProvider');
  }
  return ctx;
}
